import { NgTemplateOutlet } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AlertController,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPopover,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { ellipsisVertical } from 'ionicons/icons';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { LoaderComponent } from '../../../components/loader/loader.component';
import { OnboardingService } from 'src/app/services/onboarding.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  standalone: true,
  selector: 'app-profile',
  templateUrl: './profile.page.html',
  imports: [
    LoaderComponent,
    RouterLink,
    InlineSVGModule,
    TranslateModule,
    IonContent,
    IonBackButton,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonIcon,
    IonButtons,
    IonPopover,
    NgTemplateOutlet,
  ],
})
export class ProfilePage {
  private readonly onboardingService = inject(OnboardingService);
  private readonly userService = inject(UserService);
  readonly embedded = input(false);
  readonly user = this.userService.user;
  loading: boolean = false;
  private readonly alertCtrl = inject(AlertController);
  private readonly translate = inject(TranslateService);

  constructor() {
    addIcons({
      'ellipsis-vertical': ellipsisVertical,
    });
  }

  async remove(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('page.settings.profile.delete.title'),
      message: this.translate.instant('page.settings.profile.delete.message'),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translate.instant('page.settings.profile.delete.cancel'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('page.settings.profile.delete.confirm'),
          role: 'destructive',
          handler: () => this.deleteAccount(),
        },
      ],
    });

    await alert.present();
  }

  private async deleteAccount(): Promise<void> {
    this.loading = true;

    try {
      await this.userService.deleteAccount();
      this.onboardingService.showOnboarding();
    } finally {
      this.loading = false;
    }
  }

  async logout() {
    this.loading = true;
    try {
      await this.userService.logout();
      this.onboardingService.showOnboarding();
    } catch {
      //
    } finally {
      this.loading = false;
    }
  }
}
